import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, BedDouble, Dog, Calendar, RefreshCw, Camera, ArrowUp, ArrowDown,
  Download, AlertCircle, TrendingUp, Loader2,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Cell,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MN = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pctColor(v) {
  if (v >= 75) return 'text-emerald-600';
  if (v >= 50) return 'text-amber-600';
  return 'text-rose-600';
}

function Delta({ v, unit = 'pts', size = 'sm' }) {
  if (v === null || v === undefined || isNaN(v)) return <span className="text-gray-400">—</span>;
  const up = v >= 0;
  const sizeCls = size === 'lg' ? 'text-base' : 'text-xs';
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${up ? 'text-emerald-600' : 'text-rose-600'} ${sizeCls}`}>
      {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {Math.abs(v).toFixed(1)}{unit}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    past: { cls: 'bg-gray-100 text-gray-600 border-gray-200', label: 'Past' },
    upcoming: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Upcoming' },
    future: { cls: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Future' },
  };
  const m = map[status] || map.future;
  return <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full border ${m.cls}`}>{m.label}</span>;
}

// ═══════════════════════════════════════════════════════════════════════
// SETTINGS PANEL (room counts + daycare cap + holidays)
// ═══════════════════════════════════════════════════════════════════════
function SettingsPanel({ onClose, onSettingsSaved }) {
  const [rc, setRc] = useState({ double: 10, executive: 65, luxury: 7, single: 20 });
  const [cap, setCap] = useState(55);
  const [mode, setMode] = useState('calendar');
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [newHol, setNewHol] = useState({ name: '', icon: '📅', start_date: '', end_date: '', peak_before: 3, peak_after: 2, note: '' });
  const [snapshotting, setSnapshotting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, h] = await Promise.all([
          axios.get(`${API}/occupancy/settings`),
          axios.get(`${API}/occupancy/holidays`),
        ]);
        setRc(s.data.room_counts);
        setCap(s.data.daycare_capacity);
        setMode(s.data.daycare_denominator_mode || 'calendar');
        setHolidays(h.data.holidays || []);
      } catch {
        toast.error('Failed to load settings');
      }
    })();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/occupancy/settings`, { room_counts: rc, daycare_capacity: cap, daycare_denominator_mode: mode });
      if (res.data?.recalc_triggered) {
        toast.success('Settings saved. Recalculating historical months in the background…');
        setRecalculating(true);
        // Keep the banner up for ~90s — the background job typically finishes within that window
        // for a single location; user can refresh the page after to see updated values.
        setTimeout(() => {
          setRecalculating(false);
          onSettingsSaved?.();
        }, 90000);
      } else {
        toast.success('Occupancy settings saved');
        onSettingsSaved?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const addHoliday = async () => {
    if (!newHol.name || !newHol.start_date || !newHol.end_date) {
      toast.error('Name, start, and end date are required');
      return;
    }
    try {
      const res = await axios.post(`${API}/occupancy/holidays`, newHol);
      setHolidays([...holidays, res.data].sort((a, b) => a.start_date.localeCompare(b.start_date)));
      setNewHol({ name: '', icon: '📅', start_date: '', end_date: '', peak_before: 3, peak_after: 2, note: '' });
      toast.success('Holiday added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const deleteHoliday = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await axios.delete(`${API}/occupancy/holidays/${id}`);
      setHolidays(holidays.filter(h => h.id !== id));
      toast.success('Holiday deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const triggerSnapshot = async () => {
    setSnapshotting(true);
    try {
      await axios.post(`${API}/occupancy/snapshot`);
      toast.success('Snapshot taken');
      onSettingsSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Snapshot failed');
    }
    setSnapshotting(false);
  };

  const [backfilling, setBackfilling] = useState(false);
  const triggerBackfill = async () => {
    if (!window.confirm('Backfill historical months pulls Jan–Mar 2026 from Gingr and freezes them. This may take ~30s. Continue?')) return;
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/occupancy/backfill`, { year: new Date().getFullYear(), months: [1, 2, 3] });
      const rows = res.data.backfilled || [];
      const ok = rows.filter(r => !r.error).length;
      toast.success(`Backfilled ${ok} month${ok === 1 ? '' : 's'}: ${rows.map(r => `${r.month_name} ${r.boarding_occ}%`).join(', ')}`);
      onSettingsSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    }
    setBackfilling(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-8 pb-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4" onClick={e => e.stopPropagation()} data-testid="occupancy-settings-panel">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>Occupancy Settings</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none" data-testid="close-settings-btn">×</button>
        </div>

        <div className="px-6 py-5 space-y-6 max-h-[80vh] overflow-y-auto">
          {/* Room counts */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Boarding Room Counts</h3>
            <div className="grid grid-cols-2 gap-3">
              {[['double','Double Compartment'],['executive','Executive Room'],['luxury','Luxury Suite'],['single','Single Compartment']].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs text-gray-500 block mb-1">{label}</label>
                  <input type="number" min="0" value={rc[k]}
                    onChange={e => setRc({ ...rc, [k]: parseInt(e.target.value || '0') })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
                    data-testid={`room-count-${k}`} />
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">Total rooms: <span className="font-bold text-gray-700">{rc.double + rc.executive + rc.luxury + rc.single}</span></p>
          </div>

          {/* Daycare cap */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Daycare Capacity</h3>
            <input type="number" min="0" value={cap}
              onChange={e => setCap(parseInt(e.target.value || '0'))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
              data-testid="daycare-cap-input" />
            <p className="text-xs text-gray-500 mt-1">Daily daycare capacity goal (half-day visits count as 0.5)</p>
          </div>

          {/* Daycare denominator mode */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Daycare Occupancy Basis</h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setMode('calendar')}
                className={`p-3 rounded-lg border-2 text-left transition-all ${mode === 'calendar' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'}`}
                data-testid="daycare-mode-calendar"
              >
                <div className="font-bold text-sm text-gray-800">Calendar Days</div>
                <div className="text-[11px] text-gray-500 mt-0.5">All 30/31 days count. Sat/Sun included.</div>
                <div className="text-[10px] text-gray-400 mt-1">e.g. 55 × 30 = 1,650 cap</div>
              </button>
              <button
                onClick={() => setMode('weekdays')}
                className={`p-3 rounded-lg border-2 text-left transition-all ${mode === 'weekdays' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'}`}
                data-testid="daycare-mode-weekdays"
              >
                <div className="font-bold text-sm text-gray-800">Weekdays Only</div>
                <div className="text-[11px] text-gray-500 mt-0.5">Mon–Fri only. Weekend visits & cap excluded.</div>
                <div className="text-[10px] text-gray-400 mt-1">e.g. 55 × ~22 = 1,210 cap</div>
              </button>
            </div>
            <p className="text-xs text-amber-600 mt-2 flex items-start gap-1.5">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              Any capacity change (room counts, daycare cap, or this mode) automatically
              <strong> recalculates all closed months</strong> for the current year in the
              background. Usually completes within ~60 seconds.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button onClick={saveSettings} disabled={saving || recalculating} className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-occupancy-settings">
              {saving ? 'Saving…' : 'Save Settings'}
            </Button>
            <Button variant="outline" onClick={triggerSnapshot} disabled={snapshotting} data-testid="trigger-snapshot-btn">
              <Camera className="h-4 w-4 mr-1.5" />
              {snapshotting ? 'Taking…' : 'Take Snapshot Now'}
            </Button>
            <Button variant="outline" onClick={triggerBackfill} disabled={backfilling} data-testid="backfill-btn">
              {backfilling ? 'Backfilling…' : 'Backfill Jan–Mar'}
            </Button>
          </div>

          {recalculating && (
            <div
              className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800"
              data-testid="occupancy-recalculating-banner"
            >
              <Loader2 className="h-4 w-4 animate-spin flex-shrink-0 mt-0.5" />
              <div>
                <strong>Recalculating historical months with your new settings…</strong>
                <p className="mt-0.5 text-blue-700">
                  This runs in the background and typically finishes within ~60 seconds. Refresh
                  the Occupancy tab after to see the updated percentages.
                </p>
              </div>
            </div>
          )}
          <p className="text-xs text-gray-500 -mt-3">
            <strong>Backfill</strong> pulls Jan–Mar of the current year from Gingr and freezes them as closed months — they're never re-fetched after that, keeping the dashboard fast.
          </p>

          {/* Holidays */}
          <div className="pt-4 border-t border-gray-100">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Holiday Calendar (Global)</h3>

            <div className="space-y-1 max-h-60 overflow-y-auto mb-3">
              {holidays.map(h => (
                <div key={h.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm">
                  <span className="text-base">{h.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-800 truncate">{h.name}</div>
                    <div className="text-xs text-gray-500">{h.start_date} → {h.end_date} · peak -{h.peak_before ?? 3}/+{h.peak_after ?? 2}d</div>
                  </div>
                  <button onClick={() => deleteHoliday(h.id)} className="text-rose-500 hover:text-rose-700 text-xs px-2" data-testid={`delete-holiday-${h.id}`}>Delete</button>
                </div>
              ))}
              {!holidays.length && <p className="text-sm text-gray-400 italic">No holidays configured</p>}
            </div>

            <div className="grid grid-cols-2 gap-2 p-3 bg-emerald-50/40 border border-emerald-100 rounded-lg">
              <input placeholder="Name (e.g. July 4th)" value={newHol.name} onChange={e => setNewHol({ ...newHol, name: e.target.value })} className="col-span-2 px-3 py-1.5 border border-gray-200 rounded text-sm" data-testid="new-holiday-name" />
              <input placeholder="Icon (emoji)" value={newHol.icon} onChange={e => setNewHol({ ...newHol, icon: e.target.value })} className="px-3 py-1.5 border border-gray-200 rounded text-sm" />
              <div />
              <input type="date" value={newHol.start_date} onChange={e => setNewHol({ ...newHol, start_date: e.target.value })} className="px-3 py-1.5 border border-gray-200 rounded text-sm" data-testid="new-holiday-start" />
              <input type="date" value={newHol.end_date} onChange={e => setNewHol({ ...newHol, end_date: e.target.value })} className="px-3 py-1.5 border border-gray-200 rounded text-sm" data-testid="new-holiday-end" />
              <div>
                <label className="text-[10px] text-gray-500 block">Peak before (days)</label>
                <input type="number" min="0" max="30" value={newHol.peak_before} onChange={e => setNewHol({ ...newHol, peak_before: parseInt(e.target.value || '0') })} className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm" data-testid="new-holiday-peak-before" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block">Peak after (days)</label>
                <input type="number" min="0" max="30" value={newHol.peak_after} onChange={e => setNewHol({ ...newHol, peak_after: parseInt(e.target.value || '0') })} className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm" data-testid="new-holiday-peak-after" />
              </div>
              <input placeholder="Note (optional)" value={newHol.note} onChange={e => setNewHol({ ...newHol, note: e.target.value })} className="col-span-2 px-3 py-1.5 border border-gray-200 rounded text-sm" />
              <Button onClick={addHoliday} className="col-span-2 bg-emerald-500 hover:bg-emerald-600" data-testid="add-holiday-btn">Add Holiday</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SUB-TABS
// ═══════════════════════════════════════════════════════════════════════

function OverviewSubTab({ data, holidays, projection }) {
  const cm = data.current_month;
  if (!cm) return <p className="text-gray-500">No data yet.</p>;

  const py2025 = data.prior_year_2025.find(p => p.month === cm.month) || {};
  const snaps = data.snapshots || [];
  const lastSnap = snaps[snaps.length - 1];
  const prevSnap = snaps[snaps.length - 2];
  const prevPrevSnap = snaps[snaps.length - 3];

  const wow_b = lastSnap && prevSnap ? +(lastSnap.boarding.occupancy_rate - prevSnap.boarding.occupancy_rate).toFixed(1) : null;
  const wow_d = lastSnap && prevSnap ? +(lastSnap.daycare.occupancy_rate - prevSnap.daycare.occupancy_rate).toFixed(1) : null;
  const wow_c = lastSnap && prevSnap ? +(lastSnap.combined_occupancy_rate - prevSnap.combined_occupancy_rate).toFixed(1) : null;
  const prev_wow_b = prevSnap && prevPrevSnap ? +(prevSnap.boarding.occupancy_rate - prevPrevSnap.boarding.occupancy_rate).toFixed(1) : null;
  const prev_wow_d = prevSnap && prevPrevSnap ? +(prevSnap.daycare.occupancy_rate - prevPrevSnap.daycare.occupancy_rate).toFixed(1) : null;

  // Daily chart with holiday markers
  const monthHolidays = holidays.filter(h => {
    const start = h.start_date.slice(0, 7);
    const end = h.end_date.slice(0, 7);
    const cur = `${cm.year}-${String(cm.month).padStart(2, '0')}`;
    return start <= cur && end >= cur;
  });

  const dailyChartData = cm.daily.map(d => ({
    day: d.day,
    Boarding: d.boarding_pct,
    Daycare: d.daycare_pct,
  }));

  const todayET = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const projB = projection?.projected_boarding_pct;
  const mtdDays = (cm.daily || []).filter(d => (d.date || '') <= todayET);
  const openDays = mtdDays.filter(d => (d.daycare_used || 0) > 0);
  const avgOf = (xs, k) => xs.length ? +(xs.reduce((a, x) => a + (x[k] || 0), 0) / xs.length).toFixed(1) : null;
  const mtdB = avgOf(mtdDays, 'boarding_pct'), mtdD = avgOf(openDays, 'daycare_pct');
  const kpis = [
    { label: 'Boarding Occupancy', val: mtdB ?? cm.boarding.occupancy_rate, full: cm.boarding.occupancy_rate, py: py2025.boarding_pct, wow: wow_b, color: 'emerald', sub: `${cm.boarding.total_reservations} / ${cm.boarding.total_availability} room-nights` },
    { label: 'Daycare Occupancy', val: mtdD ?? cm.daycare.occupancy_rate, full: cm.daycare.occupancy_rate, py: py2025.daycare_pct, wow: wow_d, color: 'amber', sub: `${cm.daycare.daycare_used} / ${cm.daycare.total_availability} dog-days · ${cm.daycare.mode === 'weekdays' ? 'weekdays only' : 'all days'}` },
    { label: 'Combined Rate', val: cm.combined_occupancy_rate, py: py2025.combined_pct, wow: wow_c, color: 'violet', sub: 'All revenue streams · full month on the books', noMtd: true },
  ];

  const colorClasses = {
    emerald: { ring: 'ring-emerald-200', text: 'text-emerald-600', bar: '#10b981' },
    amber: { ring: 'ring-amber-200', text: 'text-amber-600', bar: '#f59e0b' },
    violet: { ring: 'ring-violet-200', text: 'text-violet-600', bar: '#8b5cf6' },
  };

  // Insight banner text
  let insight = null;
  if (lastSnap && prevSnap) {
    insight = (
      <span>
        <strong>Week insight ({prevSnap.snapshot_date} → {lastSnap.snapshot_date}):</strong>{' '}
        Boarding MTD {wow_b >= 0 ? 'up' : 'down'} {Math.abs(wow_b).toFixed(1)} pts.{' '}
        Daycare trending {wow_d >= 0 ? 'up' : 'down'} {Math.abs(wow_d).toFixed(1)} pts this week.{' '}
        Combined rate is {lastSnap.combined_occupancy_rate}% vs 2025 {cm.month_name} of {py2025.combined_pct ?? '—'}%.
      </span>
    );
  } else {
    insight = <span><strong>No weekly snapshots yet.</strong> Take a snapshot in Settings or wait until Sunday for the first auto-capture.</span>;
  }

  return (
    <div className="space-y-6" data-testid="occupancy-overview-tab">
      {/* Insight banner */}
      <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
        <div className="text-2xl">💡</div>
        <div className="text-sm text-gray-700 leading-relaxed">{insight}</div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {kpis.map(k => {
          const cc = colorClasses[k.color];
          const yoy = k.py != null ? +(k.val - k.py).toFixed(1) : null;
          return (
            <Card key={k.label} className={`p-5 bg-white border border-gray-100 hover:ring-2 hover:${cc.ring} transition-all`} data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{k.label}{!k.noMtd && <span className="text-gray-400"> · MTD</span>}</div>
              <div className={`v3-num text-4xl font-extrabold ${cc.text}`}>{k.val}%</div>
              {k.full != null && <div className="text-xs text-gray-600 mt-1" data-testid={`kpi-full-${k.label.toLowerCase().replace(/\s/g,'-')}`}>Full month on the books: <b className="v3-num">{k.full}%</b> · {k.sub}</div>}
              {k.label === 'Boarding Occupancy' && projB != null && <div className="text-xs text-gray-600 mt-0.5" data-testid="kpi-projected-boarding" title={`${projection.mtd_nights} nights done + ${projection.booked_future_nights} booked × ${projection.late_pickup_ratio} typical late-booking lift (from last year)`}>Projected: <b className="v3-num text-[#1F8A7C]">{projB}%</b> if pace continues</div>}
              {k.full == null && <div className="text-xs text-gray-400 mt-1">{k.sub}</div>}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                <div className="text-xs text-gray-500">
                  2025: <span className="font-semibold text-gray-700">{k.py != null ? `${k.py}%` : '—'}</span>{' '}
                  {yoy != null && <Delta v={yoy} />}
                </div>
                <div className="text-xs text-gray-500">
                  WoW: {k.wow != null ? <Delta v={k.wow} /> : <span className="text-gray-400">—</span>}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: BedDouble, label: 'Total Boarding Reservations', val: cm.boarding.total_reservations },
          { icon: Dog, label: 'Total Daycare Dog-Days', val: cm.daycare.daycare_used },
          { icon: Activity, label: 'Room-Nights Available', val: cm.boarding.total_availability },
          { icon: Calendar, label: 'Daycare Capacity Available', val: cm.daycare.total_availability },
        ].map(s => (
          <Card key={s.label} className="p-4 bg-gradient-to-br from-gray-50 to-white border border-gray-100">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <s.icon className="h-4 w-4" />
              <span className="text-[10px] font-bold uppercase tracking-wider">{s.label}</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{s.val.toLocaleString()}</div>
          </Card>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Daily occupancy */}
        <Card className="p-5 bg-white border border-gray-100">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-base font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>Daily Occupancy Rate — {cm.month_name} {cm.year}</h3>
          </div>
          <p className="text-xs text-gray-500 mb-3">Boarding vs Daycare % of capacity per day</p>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={dailyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" stroke="#94a3b8" tick={{ fontSize: 10 }} />
              <YAxis stroke="#94a3b8" tick={{ fontSize: 10 }} domain={[0, 120]} tickFormatter={v => v + '%'} />
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="Boarding" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="Daycare" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} />
              {monthHolidays.map(h => {
                const start = parseInt(h.start_date.slice(8, 10));
                const startMonth = h.start_date.slice(0, 7);
                if (startMonth === `${cm.year}-${String(cm.month).padStart(2, '0')}`) {
                  return <ReferenceLine key={h.id} x={start} stroke="#a855f7" strokeDasharray="3 3" />;
                }
                return null;
              })}
            </LineChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Boarding</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Daycare</span>
            {monthHolidays.length > 0 && <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-purple-400" style={{ borderTop: '1px dashed' }} /> Holidays</span>}
          </div>
        </Card>

        {/* Room breakdown */}
        <Card className="p-5 bg-white border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-1" style={{ fontFamily: 'Manrope' }}>Room Type Occupancy</h3>
          <p className="text-xs text-gray-500 mb-3">{cm.month_name} {cm.year} — % of capacity</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={cm.room_breakdown} layout="vertical" margin={{ left: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 10 }} domain={[0, 100]} tickFormatter={v => v + '%'} />
              <YAxis type="category" dataKey="name" stroke="#94a3b8" tick={{ fontSize: 11 }} width={140} />
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }} formatter={v => [v + '%', 'Occupancy']} />
              <Bar dataKey="occupancy_pct" radius={[0, 6, 6, 0]}>
                {cm.room_breakdown.map((r, i) => (
                  <Cell key={i} fill={r.occupancy_pct >= 75 ? '#10b981' : r.occupancy_pct >= 50 ? '#f59e0b' : '#f43f5e'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            {cm.room_breakdown.map(r => (
              <div key={r.key} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="text-gray-600 truncate">{r.name}</span>
                <span className={`font-bold ${pctColor(r.occupancy_pct)}`}>{r.occupancy_pct}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* WoW Trend panel */}
      <Card className="p-5 bg-white border border-gray-100">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>Sunday Snapshots — Week-over-Week Trend</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3">MTD cumulative occupancy rate captured every Sunday at 11:59 PM</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          {[
            { label: 'Latest WoW Boarding', v: wow_b },
            { label: 'Latest WoW Daycare', v: wow_d },
            { label: 'Prior WoW Boarding', v: prev_wow_b },
            { label: 'Prior WoW Daycare', v: prev_wow_d },
          ].map((w, i) => (
            <div key={i} className="p-3 bg-gray-50 rounded-lg">
              <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{w.label}</div>
              <Delta v={w.v} size="lg" />
            </div>
          ))}
        </div>

        {snaps.length > 1 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={snaps.map(s => ({ date: s.snapshot_date.slice(5), Boarding: s.boarding.occupancy_rate, Daycare: s.daycare.occupancy_rate, Combined: s.combined_occupancy_rate }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 10 }} />
              <YAxis stroke="#94a3b8" tick={{ fontSize: 10 }} tickFormatter={v => v + '%'} />
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }} formatter={v => [v.toFixed(1) + '%', '']} />
              <Line type="monotone" dataKey="Boarding" stroke="#10b981" strokeWidth={2} />
              <Line type="monotone" dataKey="Daycare" stroke="#f59e0b" strokeWidth={2} />
              <Line type="monotone" dataKey="Combined" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="4 4" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="p-6 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
            <Camera className="h-8 w-8 mx-auto text-gray-300 mb-2" />
            Snapshots will appear here every Sunday at 11:59 PM.<br />
            <span className="text-xs">Take a manual snapshot now from Settings to seed the trend.</span>
          </div>
        )}
      </Card>
    </div>
  );
}

function MonthlySubTab({ data }) {
  const rows = data.monthly_breakdown || [];
  return (
    <Card className="p-5 bg-white border border-gray-100" data-testid="occupancy-monthly-tab">
      <h3 className="text-base font-bold text-gray-800 mb-1" style={{ fontFamily: 'Manrope' }}>Full-Year Monthly Breakdown</h3>
      <p className="text-xs text-gray-500 mb-4">2026 actual vs 2025 — green = above prior year, red = below</p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              <th className="py-2.5 px-3 text-left">Month</th>
              <th className="py-2.5 px-3 text-right">Boarding 2026</th>
              <th className="py-2.5 px-3 text-right">Boarding 2025</th>
              <th className="py-2.5 px-3 text-right">Δ YoY</th>
              <th className="py-2.5 px-3 text-right">Daycare 2026</th>
              <th className="py-2.5 px-3 text-right">Daycare 2025</th>
              <th className="py-2.5 px-3 text-right">Δ YoY</th>
              <th className="py-2.5 px-3 text-right">Combined 2026</th>
              <th className="py-2.5 px-3 text-right">Combined 2025</th>
              <th className="py-2.5 px-3 text-right">Δ YoY</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const pairs = [
                ['boarding_2026', 'boarding_2025'],
                ['daycare_2026', 'daycare_2025'],
                ['combined_2026', 'combined_2025'],
              ];
              const rowCls = r.is_current ? 'bg-blue-50/60' : r.is_closed ? 'bg-slate-50/60' : r.is_early_data ? 'bg-emerald-50/40' : '';
              return (
                <tr key={r.month} className={`border-b border-gray-100 ${rowCls}`} data-testid={`monthly-row-${r.month}`}>
                  <td className="py-2.5 px-3 font-semibold text-gray-700">
                    {MN[r.month]}
                    {r.is_current && <Badge className="ml-2 bg-blue-500 text-white text-[10px]">CURRENT</Badge>}
                    {r.is_closed && <Badge className="ml-2 bg-slate-500 text-white text-[10px]">CLOSED</Badge>}
                    {r.is_early_data && !r.is_closed && <Badge className="ml-2 bg-emerald-500 text-white text-[10px]">EARLY</Badge>}
                  </td>
                  {pairs.map(([k26, k25]) => {
                    const v26 = r[k26], v25 = r[k25];
                    const delta = v26 != null && v25 != null ? +(v26 - v25).toFixed(1) : null;
                    const c26 = v26 != null && v25 != null ? (v26 >= v25 ? 'text-emerald-600' : 'text-rose-600') : 'text-gray-400';
                    return [
                      <td key={k26} className={`py-2.5 px-3 text-right font-bold ${c26}`}>{v26 != null ? `${v26}%` : '—'}</td>,
                      <td key={k25} className="py-2.5 px-3 text-right text-gray-600">{v25 != null ? `${v25}%` : '—'}</td>,
                      <td key={k26 + 'd'} className="py-2.5 px-3 text-right">{delta != null ? <Delta v={delta} /> : <span className="text-gray-400">—</span>}</td>,
                    ];
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function HolidaysSubTab({ data, holidays, peakAnalysis }) {
  const today = new Date();
  // Merge holidays with their peak analysis
  const byId = {};
  (peakAnalysis || []).forEach(p => { byId[p.id] = p; });
  const enriched = holidays.map(h => {
    const start = new Date(h.start_date);
    const end = new Date(h.end_date);
    let status = 'future';
    if (end < today) status = 'past';
    else if (start <= today && end >= today) status = 'upcoming';
    else if ((start - today) / (1000 * 60 * 60 * 24) < 30) status = 'upcoming';
    return { ...h, ...(byId[h.id] || {}), status, _start: start, _end: end };
  });
  enriched.sort((a, b) => a._start - b._start);

  // Find next upcoming
  const nextUp = enriched.find(h => h._start >= today);
  const daysAway = nextUp ? Math.ceil((nextUp._start - today) / (1000 * 60 * 60 * 24)) : null;

  // Match year-2025 reference rate from prior_year_actuals based on holiday's month
  const prior = data.prior_year_2025 || [];
  const py = m => (prior.find(p => p.month === m) || {}).boarding_pct;

  return (
    <div className="space-y-4" data-testid="occupancy-holidays-tab">
      <Card className="p-5 bg-white border border-gray-100">
        <h3 className="text-base font-bold text-gray-800 mb-1" style={{ fontFamily: 'Manrope' }}>Holiday & Peak Season Tracker</h3>
        <p className="text-xs text-gray-500">Peak windows extend each holiday by <strong>-3 / +2 days</strong> by default (drop-off Thursday, pickup Tuesday) — editable per holiday in Settings.</p>
      </Card>

      {nextUp && (
        <Card className="p-5 bg-gradient-to-br from-emerald-50 to-blue-50 border-2 border-emerald-200">
          <div className="flex items-start gap-4">
            <div className="text-5xl">{nextUp.icon}</div>
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">⬆ Next Upcoming Holiday</div>
              <h3 className="text-xl font-extrabold text-gray-900 mb-1" style={{ fontFamily: 'Manrope' }}>{nextUp.name}</h3>
              <p className="text-sm text-gray-600">{nextUp.start_date} → {nextUp.end_date}</p>
              <p className="text-xs text-gray-500 mt-1 italic">{nextUp.note}</p>
              <p className="text-xs text-gray-700 mt-2">2025 reference: <strong>{py(nextUp._start.getMonth() + 1) ?? '—'}%</strong> boarding rate</p>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Days away</div>
              <div className="text-4xl font-extrabold text-emerald-600">{daysAway}</div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {enriched.map(h => {
          const m = h._start.getMonth() + 1;
          const pyRate = py(m);
          const pb = h.peak_before ?? 3;
          const pa = h.peak_after ?? 2;
          const narrow = h.narrow_boarding_occupancy_pct;
          const peakOcc = h.boarding_occupancy_pct;
          const peakDay = h.peak_day;
          const peakDayPct = h.peak_day_pct;
          const daycarePct = h.daycare_occupancy_pct;
          return (
            <Card key={h.id} className={`p-4 border ${h.status === 'past' ? 'bg-gray-50/60 border-gray-200' : h.status === 'upcoming' ? 'bg-emerald-50/40 border-emerald-200' : 'bg-white border-gray-100'}`} data-testid={`holiday-card-${h.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{h.icon}</span>
                  <div>
                    <div className="font-bold text-gray-800">{h.name}</div>
                    <div className="text-xs text-gray-500">{h.start_date} → {h.end_date}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      Peak window: {h.peak_start} → {h.peak_end} (-{pb}/+{pa}d)
                    </div>
                  </div>
                </div>
                <StatusBadge status={h.status} />
              </div>
              {h.note && <p className="text-xs text-gray-500 italic mb-2">{h.note}</p>}
              {narrow != null ? (
                <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-gray-100">
                  <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase">Narrow Boarding</div>
                    <div className={`text-sm font-bold ${pctColor(narrow)}`}>{narrow}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase">Peak Window</div>
                    <div className={`text-sm font-bold ${pctColor(peakOcc)}`}>{peakOcc}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase">2025 Ref</div>
                    <div className="text-sm font-bold text-gray-700">{pyRate != null ? `${pyRate}%` : '—'}</div>
                  </div>
                  {peakDay && (
                    <div className="col-span-3 mt-1 pt-1 border-t border-dashed border-gray-200 text-[11px] text-gray-600">
                      Busiest day: <strong className={pctColor(peakDayPct)}>{peakDay} ({peakDayPct}%)</strong>
                      {daycarePct != null && <span className="text-gray-400 ml-2">· Daycare peak: {daycarePct}%</span>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-100">
                  <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase">2026 Data</div>
                    <div className="text-sm font-bold text-gray-400">Future</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase">2025 Reference</div>
                    <div className="text-sm font-bold text-gray-700">{pyRate != null ? `${pyRate}%` : '—'}</div>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function ArchiveSubTab({ data }) {
  const snaps = data.snapshots || [];

  const exportCsv = () => {
    if (!snaps.length) {
      toast.error('No snapshots to export');
      return;
    }
    const header = ['Snapshot Date', 'Type', 'Year', 'Month', 'ISO Week', 'Boarding %', 'Daycare %', 'Combined %', 'Boarding Reservations', 'Daycare Used'];
    const rows = snaps.map(s => [
      s.snapshot_date,
      s.snapshot_type,
      s.year,
      s.month_name,
      s.iso_week,
      s.boarding.occupancy_rate,
      s.daycare.occupancy_rate,
      s.combined_occupancy_rate,
      s.boarding.total_reservations,
      s.daycare.daycare_used,
    ]);
    const csv = [header, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `occupancy-snapshots-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="p-5 bg-white border border-gray-100" data-testid="occupancy-archive-tab">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-base font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>Sunday Snapshot Archive</h3>
          <p className="text-xs text-gray-500">Every Sunday at 11:59 PM (location time) — immutable weekly records</p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
          <Download className="h-4 w-4 mr-1.5" />
          Export CSV
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              <th className="py-2.5 px-3 text-left">Date</th>
              <th className="py-2.5 px-3 text-left">Month</th>
              <th className="py-2.5 px-3 text-right">Boarding</th>
              <th className="py-2.5 px-3 text-right">Daycare</th>
              <th className="py-2.5 px-3 text-right">Combined</th>
              <th className="py-2.5 px-3 text-center">Type</th>
              <th className="py-2.5 px-3 text-right">WoW Δ Boarding</th>
              <th className="py-2.5 px-3 text-right">WoW Δ Daycare</th>
            </tr>
          </thead>
          <tbody>
            {snaps.length === 0 && (
              <tr><td colSpan={8} className="py-8 text-center text-gray-400 italic">
                No snapshots yet. Snapshots are taken automatically every Sunday at 11:59 PM, or trigger a manual snapshot from Settings.
              </td></tr>
            )}
            {snaps.map((s, i) => {
              const prev = snaps[i - 1];
              const wb = prev ? +(s.boarding.occupancy_rate - prev.boarding.occupancy_rate).toFixed(1) : null;
              const wd = prev ? +(s.daycare.occupancy_rate - prev.daycare.occupancy_rate).toFixed(1) : null;
              const isLast = i === snaps.length - 1;
              return (
                <tr key={s.id} className={`border-b border-gray-100 ${isLast ? 'bg-blue-50/40' : ''}`} data-testid={`snapshot-row-${s.id}`}>
                  <td className="py-2.5 px-3 font-semibold text-gray-700">{s.snapshot_date}</td>
                  <td className="py-2.5 px-3 text-gray-600">{s.month_name} {s.year}</td>
                  <td className={`py-2.5 px-3 text-right font-bold ${pctColor(s.boarding.occupancy_rate)}`}>{s.boarding.occupancy_rate}%</td>
                  <td className={`py-2.5 px-3 text-right font-bold ${pctColor(s.daycare.occupancy_rate)}`}>{s.daycare.occupancy_rate}%</td>
                  <td className={`py-2.5 px-3 text-right font-bold ${pctColor(s.combined_occupancy_rate)}`}>{s.combined_occupancy_rate}%</td>
                  <td className="py-2.5 px-3 text-center">
                    <Badge variant="outline" className={`text-[10px] ${s.snapshot_type === 'weekly_auto' ? 'text-blue-600 border-blue-200' : 'text-amber-600 border-amber-200'}`}>
                      {s.snapshot_type === 'weekly_auto' ? 'WEEKLY AUTO' : 'MANUAL'}
                    </Badge>
                  </td>
                  <td className="py-2.5 px-3 text-right">{wb != null ? <Delta v={wb} /> : <span className="text-gray-400">—</span>}</td>
                  <td className="py-2.5 px-3 text-right">{wd != null ? <Delta v={wd} /> : <span className="text-gray-400">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-start gap-2 text-xs text-blue-800">
        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <p>Snapshots are stored permanently in MongoDB (<code>weekly_snapshots</code>). Each Sunday's snapshot is immutable — never overwritten. Used for week-over-week comparisons across the dashboard.</p>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════
export default function OccupancyTrend() {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [data, setData] = useState(null);
  const [projection, setProjection] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [peakAnalysis, setPeakAnalysis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    // Fetch each endpoint independently so a failure in one (e.g. transient peak-analysis
    // error from Gingr throttling) doesn't blank the whole tab.
    const [dRes, hRes, pRes] = await Promise.allSettled([
      axios.get(`${API}/occupancy/dashboard${refresh ? '?refresh=true' : ''}`),
      axios.get(`${API}/occupancy/holidays`),
      axios.get(`${API}/occupancy/holidays/peak-analysis`),
    ]);
    axios.get(`${API}/occupancy/projection`).then(r => setProjection(r.data)).catch(() => {});
    if (dRes.status === 'fulfilled') {
      setData(dRes.value.data);
    } else if (!dataRef.current) {
      // Only show the error if we have nothing to display
      toast.error(dRes.reason?.response?.data?.detail || 'Failed to load occupancy data');
    }
    if (hRes.status === 'fulfilled') setHolidays(hRes.value.data.holidays || []);
    if (pRes.status === 'fulfilled') setPeakAnalysis(pRes.value.data.holidays || []);
    setLoading(false);
    setRefreshing(false);
  }, []);

  // Keep a ref of `data` so `load` doesn't need it as a dep (which would cause an infinite render loop).
  const dataRef = useRef(null);
  useEffect(() => { dataRef.current = data; }, [data]);

  useEffect(() => { load(); }, [load]);

  // Auto-revalidate when the tab becomes visible again or window refocuses.
  // Catches the Sunday-night auto-snapshot case: user has Occupancy open before
  // 11:59 PM, returns to the tab Monday morning — without this, the WoW section
  // would still render against the stale pre-snapshot data.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        // Soft refresh — bypass backend cache to guarantee newest snapshots
        load(true);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500" data-testid="occupancy-loading">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Loading occupancy data…
      </div>
    );
  }

  if (!data || data?.current_month?.error === 'Gingr not configured') {
    return (
      <Card className="p-8 text-center bg-amber-50 border-amber-200" data-testid="occupancy-error">
        <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
        <h3 className="font-bold text-amber-800 mb-1">Gingr not configured for this location</h3>
        <p className="text-sm text-amber-700">Set up the Gingr integration in Settings to start tracking occupancy.</p>
      </Card>
    );
  }

  const subTabs = [
    { value: 'overview', label: 'Overview', Icon: Activity },
    { value: 'monthly', label: 'Monthly Breakdown', Icon: Calendar },
    { value: 'holidays', label: 'Holidays & Peaks', Icon: TrendingUp },
    { value: 'archive', label: 'Snapshot Archive', Icon: Camera },
  ];

  return (
    <div className="space-y-4" data-testid="occupancy-trend-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>Occupancy Trend</h2>
          <p className="text-xs text-gray-500">
            Live from Gingr • Last refreshed {data.last_refreshed_at ? new Date(data.last_refreshed_at).toLocaleString() : 'just now'}
            {data.cached && ' (cached)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="refresh-occupancy-btn"
            title="Refresh data"
            aria-label="Refresh data"
            className="h-9 w-9"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="open-occupancy-settings">
            ⚙ Settings
          </Button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="bg-white border border-gray-200 rounded-xl p-1.5 inline-flex gap-1.5 flex-wrap" data-testid="occupancy-subtabs">
        {subTabs.map(t => (
          <button
            key={t.value}
            onClick={() => setActiveSubTab(t.value)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
              activeSubTab === t.value
                ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
            data-testid={`occupancy-subtab-${t.value}`}
          >
            <t.Icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {activeSubTab === 'overview' && <OverviewSubTab data={data} holidays={holidays} projection={projection} />}
      {activeSubTab === 'monthly' && <MonthlySubTab data={data} />}
      {activeSubTab === 'holidays' && <HolidaysSubTab data={data} holidays={holidays} peakAnalysis={peakAnalysis} />}
      {activeSubTab === 'archive' && <ArchiveSubTab data={data} />}

      {showSettings && (
        <SettingsPanel onClose={() => setShowSettings(false)} onSettingsSaved={() => load(true)} />
      )}
    </div>
  );
}
